import type { HTMLAttributes, ReactNode } from "react";
import { cx } from "./cx";
import { Button, IconButton } from "./button";
import { CircleAlert, CircleCheck, Icon, Info, TriangleAlert, X } from "./icon";

export type ToastVariant = "info" | "success" | "warning" | "danger";

const TOAST_ICON = {
  info: Info,
  success: CircleCheck,
  warning: TriangleAlert,
  danger: CircleAlert,
} as const;

/** Optional inline action (e.g. "Undo" on a reversible toast, before commit). */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastProps {
  variant?: ToastVariant;
  title: ReactNode;
  message?: ReactNode;
  action?: ToastAction;
  onDismiss?: () => void;
  className?: string;
}

export function Toast({ variant = "info", title, message, action, onDismiss, className }: ToastProps) {
  return (
    <div role="status" className={cx("pz-toast", `pz-toast--${variant}`, className)}>
      <span className="pz-toast__icon">
        <Icon icon={TOAST_ICON[variant]} size="lg" />
      </span>
      <div>
        <p className="pz-toast__title">{title}</p>
        {message != null && <p className="pz-toast__message">{message}</p>}
        {action && (
          <span className="pz-toast__action">
            <Button variant="ghost" size="sm" onClick={action.onClick}>
              {action.label}
            </Button>
          </span>
        )}
      </div>
      {onDismiss && (
        <span className="pz-toast__close">
          <IconButton label="Dismiss" variant="ghost" size="sm" onClick={onDismiss}>
            <Icon icon={X} size="sm" />
          </IconButton>
        </span>
      )}
    </div>
  );
}

export interface ToastRegionProps extends HTMLAttributes<HTMLDivElement> {
  /** "inline" renders in-flow (gallery); default stacks fixed at z.toast. */
  layout?: "viewport" | "inline";
}

export function ToastRegion({ layout = "viewport", className, ...rest }: ToastRegionProps) {
  return (
    <div
      className={cx("pz-toast-region", layout === "inline" && "pz-toast-region--inline", className)}
      aria-live="polite"
      {...rest}
    />
  );
}
